/**
 * Radial (tainter) spillway gates (HYDRAULICS §3.1–3.4).
 *
 * Free flow (lip clear of the water, the ogee crest controls):
 *     Q = C · L · H_e^1.5                      H_e = HW − sill
 * Orifice flow (gate in the water, the opening controls):
 *     Q = C_g · L · a · √(2g·(H_e − a/2))
 *
 * `C_g = C/√g` makes the two agree exactly at a = H_e, so the transition is analytically
 * continuous at every headwater rather than merely close (DECISIONS D11). The `a ≥ H_e` branch is
 * what keeps Q(a) monotone at low pool, where the orifice head term would otherwise go negative.
 */

import { G, clamp, gsqrt } from './constants';
import type { GateState, SiteConfig } from './types';

/** Free-flow (crest-controlled) discharge for one gate, m³/s. */
export function freeFlow(site: SiteConfig, hw: number): number {
  const he = hw - site.spillway.sill;
  if (he <= 0) return 0;
  return site.spillway.dischargeCoeff * site.spillway.gateWidth * Math.pow(he, 1.5);
}

/** Orifice (gate-controlled) discharge for one gate, m³/s. Valid for a < H_e. */
export function orificeFlow(site: SiteConfig, hw: number, opening: number): number {
  const he = hw - site.spillway.sill;
  if (he <= 0 || opening <= 0) return 0;
  const a = Math.min(opening, he);
  return site.spillway.orificeCoeff * site.spillway.gateWidth * a * gsqrt(2 * G * (he - a / 2));
}

/**
 * Discharge for one gate at a given headwater and opening, m³/s.
 * Monotone and continuous in `opening` at every headwater — asserted in tests/gates.test.ts.
 */
export function gateDischarge(site: SiteConfig, hw: number, opening: number): number {
  const he = hw - site.spillway.sill;
  if (he <= 0 || opening <= 0) return 0;
  const free = freeFlow(site, hw);
  if (opening >= he) return free; // lip clear of the water — the crest controls
  return Math.min(free, orificeFlow(site, hw, opening));
}

/**
 * Flow over the top of a gate leaf, m³/s. Broad-crested weir over the leaf top, which sits at
 * `sill + gateHeight + opening`. Non-zero here is an Emergency dam-safety condition.
 */
export function overtopFlow(site: SiteConfig, hw: number, opening: number): number {
  const top = site.spillway.sill + site.spillway.gateHeight + opening;
  if (hw <= top) return 0;
  return site.spillway.overtopCoeff * site.spillway.gateWidth * Math.pow(hw - top, 1.5);
}

/** Total spillway discharge and the per-gate breakdown. */
export function totalSpill(
  site: SiteConfig,
  hw: number,
  gates: GateState[],
): { total: number; perGate: number[]; overtopping: boolean } {
  const perGate: number[] = [];
  let total = 0;
  let overtopping = false;
  for (const g of gates) {
    let q = gateDischarge(site, hw, g.opening);
    if (g.damaged) q = Math.min(q, site.spillway.damagedGateFlowCap);
    const over = overtopFlow(site, hw, g.opening);
    if (over > 0) overtopping = true;
    q += over;
    perGate.push(q);
    total += q;
  }
  return { total, perGate, overtopping };
}

/**
 * Opening that passes a requested discharge at a given headwater, m.
 * Fixed 40-iteration bisection — never a convergence loop, so replay stays bit-identical
 * (ARCHITECTURE, "Determinism" #3). Returns the gate height if the request exceeds capacity.
 */
export function openingForDischarge(site: SiteConfig, hw: number, qTarget: number): number {
  const maxA = site.spillway.gateHeight;
  if (qTarget <= 0) return 0;
  if (gateDischarge(site, hw, maxA) <= qTarget) return maxA;
  let lo = 0;
  let hi = maxA;
  for (let i = 0; i < 40; i++) {
    const mid = (lo + hi) / 2;
    if (gateDischarge(site, hw, mid) < qTarget) lo = mid;
    else hi = mid;
  }
  return (lo + hi) / 2;
}

/**
 * Move one gate toward its target at the hoist rate. Failures freeze the leaf where it is;
 * cold gates need `heaterPreheatMinutes` of heater before they will move at all.
 */
export function stepGateActuator(
  site: SiteConfig,
  gate: GateState,
  dtS: number,
  airTempC: number,
): void {
  if (gate.hoist !== 'ok') return;
  if (airTempC < site.spillway.iceThresholdC && gate.heaterMinutes < site.spillway.heaterPreheatMinutes) {
    return; // iced in place until the heaters have run long enough (E11)
  }
  const maxStep = (site.spillway.hoistRate * dtS) / 60;
  const target = clamp(gate.target, 0, site.spillway.gateHeight);
  const delta = target - gate.opening;
  if (Math.abs(delta) <= maxStep) gate.opening = target;
  else gate.opening += Math.sign(delta) * maxStep;
  gate.opening = clamp(gate.opening, 0, site.spillway.gateHeight);
}

/** Largest opening difference between adjacent gates, m. The balanced-operation rule (§2.1). */
export function maxAdjacentImbalance(gates: GateState[]): number {
  let worst = 0;
  for (let i = 1; i < gates.length; i++) {
    worst = Math.max(worst, Math.abs(gates[i]!.opening - gates[i - 1]!.opening));
  }
  return worst;
}
